// @givmo/board — EVERY word CBG-OPS1-CIRC-WEB's screens add, in one place: whether each director was
// emailed when a written consent went out (CBG-OPS1-CIRC; backend
// `agent_docs/contracts/board-consent-circulation.md` §8).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the frames for building on 2026-10-13 22:40 PDT (`k1-circulate` and
// `k2-roster`, design stop design-131) with every word kept a draft, and then the words themselves in
// this row's copy round on 2026-10-14 09:53 PDT ("go with your recs"): every line here, as written on
// its copy page. They are this row's own words. The two dialog lines say what the mail says, not in
// its words: the mail's are CBG-OPS1-COPY2's, and only the backend sends them. "Hasn’t joined Board
// yet" is CBG-YR2-COPY1's, approved 2026-10-13 09:27 PDT, and comes from its one definition. Each line
// names the frame that draws it, or "not drawn" for a state no frame shows.
//
// ⚠ Plan §3.2 and §3.4 bind every line. "Emailed" says Board handed the message to the mail service,
// never that anyone read it: a provider accepting a message is not evidence a director saw it, so no
// line claims more. Every reason a mail didn't go reads the same (the orchestrator's K-C1): the
// officer's act is the same whatever the code, and naming the environment's guard or the console's
// address would put plumbing on the page. A director is "they", never a gendered pronoun.

import { coiWords } from '../conflicts/coi-words';

/** Drawn, k2-roster ("Emailed Feb 15, 2027"). */
const emailed = (on: string) => `Emailed ${on}`;

export const consentDeliveryWords = {
  // ── Before it goes out ──────────────────────────────────────────────────────────────────────────

  // Drawn, k1-circulate: the circulate dialog's last line (it replaces "This doesn’t email anyone.
  // Send each of them their own board link when you’re ready for them to sign.").
  circulateEmails:
    'Each director gets an email saying a written consent is waiting for their signature.',
  // Not drawn: the revise dialog says the same of a revision (the derivation's §6.2).
  reviseEmails:
    'Each director gets an email saying the revised consent is waiting for their signature.',

  // ── Each director, once it went out (the consent page's "Who has to sign") ──────────────────────

  // "Emailed Feb 15, 2027" (above), and after " · " the approved "Hasn’t joined Board yet"
  // (CBG-YR2-COPY1, from its one definition). Drawn, k2-roster: Amara Nwosu's line, whose email
  // pointed them to their invitation. The second part is said only while the seat is still unjoined
  // (the orchestrator's ruling, 2026-10-14), because its words are present tense. Not drawn: a seat
  // that joined since, which reads "Emailed Feb 15, 2027" alone.
  emailedLine: (on: string, stillUnjoined: boolean): string =>
    stillUnjoined ? `${emailed(on)} · ${coiWords.hasntJoined}` : emailed(on),
  // Drawn, k2-roster (Henrik Sato), for every reason a mail didn't go. Said only while the seat still
  // has to sign a consent that is out; the page decides when (`DeliveryLine`).
  notSent: 'The email didn’t go out. Let them know yourself.',
  // Not drawn: the mail was to go and no outcome was recorded.
  notConfirmed: 'We can’t confirm the email went out.',

  // ── The person's own Board home (CBG-FIX107) ────────────────────────────────────────────────────

  // Approved 2026-10-17 22:51 PDT (design-225's copy page, row 1b.1; frames b1, m1): the row for the
  // written consents waiting for this person's signature on one board, in the circulation email's
  // own words (CBG-OPS1-COPY2: "The {Org} board has a written consent waiting for your signature.";
  // CBG-FIX143's house form).
  // Nothing of the consent: no title, no text, no date (the email's rule, §N8). Several on one board
  // (not drawn) count themselves.
  homeWaiting: (count: number) =>
    count === 1
      ? 'A written consent waiting for your signature'
      : `${count} written consents waiting for your signature`,
  // Approved with them (row 1b.1, not drawn): one committee's consent, named as that committee's
  // meetings are on the same list.
  homeWaitingCommittee: (committee: string) =>
    `${committee} · A written consent waiting for your signature`,
};
